import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AccountHolderDetailsResponse, AccountHolderStore } from '../../../types/features/accountHolders';
import accountHoldersService from '../services/accountHoldersService';

export const useAccountHolderDetailsPageLogic = () => {
  const navigate = useNavigate();
  const { holderId } = useParams<{ holderId: string }>();
  const requestIdRef = useRef(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [ownerDetails, setOwnerDetails] = useState<AccountHolderDetailsResponse | null>(null);

  useEffect(() => {
    const loadOwnerStores = async () => {
      const requestId = ++requestIdRef.current;

      if (!holderId) {
        setOwnerDetails(null);
        setLoading(false);
        setError('Invalid account holder id.');
        return;
      }

      try {
        setLoading(true);
        setError('');
        setOwnerDetails(null);
        const response = await accountHoldersService.getAccountHolderDetails(holderId);
        if (requestId !== requestIdRef.current) {
          return;
        }
        setOwnerDetails(response);
      } catch (_error: unknown) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setOwnerDetails(null);
        setError('Failed to load account holder details. Please try again.');
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    loadOwnerStores();
  }, [holderId]);

  const stores = useMemo<AccountHolderStore[]>(() => ownerDetails?.stores || [], [ownerDetails]);

  const handleStoreClick = useCallback(
    (storeId: string) => {
      navigate(`/dashboard/store-summary/stores/${storeId}`);
    },
    [navigate],
  );

  return {
    loading,
    error,
    ownerDetails,
    stores,
    handleStoreClick,
  };
};
